import React, { useMemo, useRef, useState } from "react";
import NumberFormat from "react-number-format";

import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import { Box, Button, IconButton, Paper, Stack, TextField } from "@mui/material";
import { ValidationError } from "components";
import { CreateNewInvestorDrawer } from "components/deal/Investors/Create-new-investor-drawer/CreateNewInvestorDrawer";
import { SelectAutocomplete } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDidUpdate } from "hooks";
import { debounce } from "lodash";
import { useInvestorsSelector } from "store";

import { AttachFiles } from "./AttachFiles";
import { CalculateButton } from "./CalculateButton";
import { Errors, IGroupsFormContext, MeasureData as IMeasureData, useGroupsForm } from "./GroupsFormContext";

export interface Props {
  measureData: IMeasureData;
  hideCalculateButton?: boolean;
  valuePrefix?: string;
  valueSuffix?: string;
  deal_id: number;
  calculateMeasureData?: IGroupsFormContext["calculateMeasureData"];
  isAllowedNegative: boolean;
  parentDealId: number | null;
  isMeasureGlobalValue: boolean;
}

export const MeasureData: React.FC<Props> = ({
  hideCalculateButton = false,
  valuePrefix = "",
  valueSuffix = "",

  deal_id,
  calculateMeasureData,
  isAllowedNegative,
  measureData,
  parentDealId,
  isMeasureGlobalValue
}) => {
  const { key, value, investor_id, group_id, investor_name, description, enable_split, round_id, isDeletable, files } =
    measureData;

  const investors = useInvestorsSelector().map((item) => ({ id: item.id, name: item.name, group: item.group || null }));
  const {
    updateMeasureData,
    setCommentPopup,
    getMeasureDataErrors,
    deleteMeasureData,
    setMeasureDataFilesPickerParam
  } = useGroupsForm();
  const isExistingRound = typeof round_id === "number";

  const { value: valueError = [], entity = [] } = getMeasureDataErrors(key) || {};
  const [localValue, setLocalValue] = useState<string>(value);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [investorError, setInvestorError] = useState<string>("");
  const [selectedInvestor, setSelectedInvestor] = useState<{ id: number; name: string; group: string | null } | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  const updateMeasureDataDebounce = useMemo(() => debounce(updateMeasureData, 300), [updateMeasureData]);

  useDidUpdate(() => {
    if (value === localValue) return;
    setLocalValue(value);
  }, [value]);

  const handleInvestorSelect = ({
    investor_id,
    investor_name,
    investor_group
  }: {
    investor_id: number;
    investor_name: string;
    investor_group: string | null;
  }) => {
    try {
      updateMeasureData({
        key,
        investor_id,
        investor_name
      });
      setSelectedInvestor(null);
      setInvestorError("");
      inputRef.current?.blur();
    } catch (error) {
      if (error === Errors.SAME_INVESTOR_PER_ROUND) {
        setSelectedInvestor({ id: investor_id, name: investor_name, group: investor_group || null });
        setInvestorError(Errors.SAME_INVESTOR_PER_ROUND);
      }
    }
  };

  return (
    <Stack gap="5px">
      <Stack
        direction="row"
        sx={{
          // alignItems: "center",
          alignItems: "flex-start",
          gap: "10px",
          ".MuiIconButton-root": {
            opacity: 0,
            transition: "all 0.2s linear"
          },
          ":hover": {
            ".MuiIconButton-root": {
              opacity: 1
            }
          }
        }}
      >
        <Box sx={{ width: isMeasureGlobalValue ? 22 : 35, height: 24 }}>
          {Boolean(isDeletable) && (
            <IconButton
              onClick={() => deleteMeasureData(key)}
              size="sm"
              sx={{ p: 0, opacity: "1 !important", alignSelf: "center" }}
            >
              <RemoveOutlinedIcon />
            </IconButton>
          )}
        </Box>

        {!isMeasureGlobalValue && (
          <>
            {investor_id ? (
              <Stack
                sx={{
                  height: 40,
                  width: 180,
                  minWidth: 180,
                  p: "0 16px",
                  bgcolor: "#F5F7F7",
                  borderRadius: "8px",
                  justifyContent: "center"
                }}
              >
                <MaterialTooltip title={investor_name} isShow={Boolean(investor_name)}>
                  <span
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%"
                    }}
                  >
                    {investor_name}
                  </span>
                </MaterialTooltip>
              </Stack>
            ) : (
              <SelectAutocomplete
                sx={{ maxWidth: 195 }}
                options={investors}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    inputRef={inputRef}
                    {...params}
                    label="Investor"
                    placeholder="Type to add investors"
                    error={Boolean(investorError?.length)}
                    helperText={investorError}
                  />
                )}
                renderOption={(
                  props: React.HTMLAttributes<HTMLLIElement>,
                  option: { id: number; name: string; group: string | null }
                ) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                setValue={(value) => {
                  if (!value) return;
                  handleInvestorSelect({
                    investor_id: value.id,
                    investor_name: value.name,
                    investor_group: value.group || null
                  });
                }}
                value={selectedInvestor}
                getOptionLabel={(option) => option.name}
                compareParam={(p) => p.name}
                slotProps={{
                  paper: {
                    component: ({ children, ...props }: any) => {
                      return (
                        <Paper
                          {...props}
                          sx={{
                            position: "absolute",
                            width: "100%",
                            backgroundColor: "white",
                            zIndex: "10",
                            borderRadius: "10px",
                            "& .MuiAutocomplete-listbox": {
                              maxHeight: "28vh",
                              "& .MuiAutocomplete-option[aria-disabled='true']": {
                                opacity: 1,
                                "&.Mui-focused": {
                                  opacity: 1
                                }
                              },
                              "& .MuiAutocomplete-option[aria-selected='true']": {
                                bgcolor: "#edf1f9",
                                "&.Mui-focused": {
                                  bgcolor: "#edf1f9"
                                }
                              }
                            },
                            "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
                              bgcolor: "#edf1f9"
                            }
                          }}
                        >
                          <>
                            {children}
                            <Button
                              onMouseDown={() => setIsDrawerOpen(true)}
                              sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                height: "50px",
                                backgroundColor: "#fff",
                                boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                                borderRadius: "0 0 10px 10px"
                              }}
                              fullWidth
                            >
                              + Create New Investor
                            </Button>
                          </>
                        </Paper>
                      );
                    }
                  }
                }}
              />
            )}
          </>
        )}
        <NumberFormat
          thousandSeparator
          isNumericString
          variant="outlined"
          prefix={valuePrefix}
          suffix={valueSuffix}
          decimalScale={2}
          allowNegative={isAllowedNegative}
          error={Boolean(valueError.length) || Boolean(entity.length)}
          // helperText={valueError}
          label="Value"
          sx={{
            flexGrow: 1,
            ".MuiInputBase-root": {
              pr: 0,
              overflow: "hidden"
            },
            ".MuiInputBase-input": {
              padding: "9.5px 8px"
            },
            ".MuiButtonBase-root": {
              visibility: "hidden",
              opacity: 0,
              transition: "opacity 0.2s ease"
            },
            "&:hover": {
              ".MuiButtonBase-root": {
                visibility: "visible",
                opacity: 1
              }
            }
          }}
          {...(!hideCalculateButton &&
            enable_split &&
            isExistingRound && {
              InputProps: {
                endAdornment: (
                  <CalculateButton
                    onClick={() => {
                      if (!round_id || !deal_id || !value || !investor_id) return;
                      calculateMeasureData?.({
                        round_id: round_id,
                        split_type: "by_investor",
                        investor_id: investor_id,
                        ...(parentDealId && { parent_deal_id: parentDealId }),
                        group_id,
                        value
                      });
                    }}
                  />
                )
              }
            })}
          // allowNegative={isNegativeAllowed}
          customInput={TextField}
          value={localValue}
          onValueChange={({ value }) => {
            setLocalValue(value);
            updateMeasureDataDebounce({ key, value });
          }}
        />
        <AttachFiles
          isTotal={false}
          isMeasureGlobalValue={isMeasureGlobalValue}
          files={files}
          setMeasureDataFilesPickerParam={setMeasureDataFilesPickerParam}
          group_id={group_id}
          measureDataKey={key}
          roundId={round_id}
        />
        <IconButton
          sx={{ mt: "6px" }}
          onClick={(e) =>
            setCommentPopup({ measureDataKey: key, initialValue: description || "", anchorEl: e.currentTarget })
          }
        >
          <ChatOutlinedIcon />
        </IconButton>
      </Stack>
      {<ValidationError messages={[...valueError, ...entity]} />}
      <CreateNewInvestorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        saveNewInvestorToSelected={(response) => {
          handleInvestorSelect({
            investor_id: response.id,
            investor_name: response.name,
            investor_group: response.group || null
          });
        }}
      />
    </Stack>
  );
};
